import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import SvgEditor from './SvgEditor';

const SvgToolbar = ({ targetId, onOpen, onClose }) => {
  const [showModal, setShowModal] = useState(false);
  const [initialSvg, setInitialSvg] = useState('');
  const [replaceRange, setReplaceRange] = useState(null);
  const [activeShortcode, setActiveShortcode] = useState(null);

  const openModal = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    
    if (!targetId) {
      alert('Please click into a text field first to specify where to insert the drawing.');
      return;
    }

    const input = document.getElementById(targetId);
    let svgContent = '';
    let startPos = -1;
    let endPos = -1;
    let foundShortcode = null;
    
    if (input) {
      const currentVal = input.value || '';
      const selStart = input.selectionStart || 0;
      const selEnd = input.selectionEnd || 0;

      const selectedText = currentVal.substring(selStart, selEnd).trim();
      
      // 1. Check if selected text is a diagram shortcode
      if (selectedText.startsWith('[SVG-') && selectedText.endsWith(']')) {
        foundShortcode = selectedText;
        startPos = selStart;
        endPos = selEnd;
      } else {
        const lastOpen = currentVal.lastIndexOf('[SVG-', selStart);
        if (lastOpen !== -1) {
          const nextClose = currentVal.indexOf(']', lastOpen);
          if (nextClose !== -1 && nextClose + 1 >= selEnd) {
            foundShortcode = currentVal.substring(lastOpen, nextClose + 1);
            startPos = lastOpen;
            endPos = nextClose + 1;
          }
        }
      }

      if (foundShortcode && window.svgDiagrams && window.svgDiagrams[foundShortcode]) {
        svgContent = window.svgDiagrams[foundShortcode];
        setActiveShortcode(foundShortcode);
      } else {
        // 2. Fall back to standard SVG XML search
        setActiveShortcode(null);
        if (selectedText.startsWith('<svg') && selectedText.indexOf('</svg>') !== -1) {
          svgContent = selectedText;
          startPos = selStart;
          endPos = selEnd;
        } else {
          const lastSvgOpen = currentVal.lastIndexOf('<svg', selStart);
          if (lastSvgOpen !== -1) {
            const nextSvgClose = currentVal.indexOf('</svg>', lastSvgOpen);
            if (nextSvgClose !== -1 && nextSvgClose + 6 >= selEnd) {
              svgContent = currentVal.substring(lastSvgOpen, nextSvgClose + 6);
              startPos = lastSvgOpen;
              endPos = nextSvgClose + 6;
            }
          }
        }
      }
    }
    
    if (svgContent) {
      setInitialSvg(svgContent);
      setReplaceRange({ start: startPos, end: endPos });
    } else {
      setInitialSvg('');
      setReplaceRange(null);
      setActiveShortcode(null);
    }

    setShowModal(true);
    if (onOpen) onOpen();
  };

  const handleInsertToMain = (svgHtml) => {
    try {
      const input = document.getElementById(targetId);
      if (!input) {
        setShowModal(false);
        return;
      }

      window.svgDiagrams = window.svgDiagrams || {};
      
      let shortcode = activeShortcode;
      if (!shortcode) {
        const nextIdx = Object.keys(window.svgDiagrams).length + 1;
        shortcode = `[SVG-${nextIdx}]`;
      }
      
      window.svgDiagrams[shortcode] = svgHtml;

      const start = replaceRange ? replaceRange.start : (input.selectionStart || 0);
      const end = replaceRange ? replaceRange.end : (input.selectionEnd || 0);
      const currentVal = input.value || '';

      const newVal = replaceRange
        ? currentVal.substring(0, start) + shortcode + currentVal.substring(end)
        : currentVal.substring(0, start) + "\n" + shortcode + "\n" + currentVal.substring(end);

      // Safe way to trigger React state update
      const setter = input.tagName === 'TEXTAREA' 
        ? Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set
        : Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;

      if (setter) {
        setter.call(input, newVal);
      } else {
        input.value = newVal;
      }

      // Dispatch event to make sure React picks it up
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));

      // Move cursor to end of inserted text
      const newPos = start + shortcode.length + (replaceRange ? 0 : 2);
      try {
        input.setSelectionRange(newPos, newPos);
      } catch (e) {}
      
      input.focus();
      setShowModal(false);
      if (onClose) onClose();
    } catch (err) {
      console.error('Final insert error:', err);
      setShowModal(false);
      if (onClose) onClose();
    }
  };

  useEffect(() => {
    const handleEditSvg = (e) => {
      if (e.detail.targetId === targetId) {
        setInitialSvg(e.detail.svgHtml);

        const input = document.getElementById(targetId);
        if (input) {
          const currentVal = input.value || '';
          let matchedShortcode = null;
          
          const parser = new DOMParser();
          const targetDoc = parser.parseFromString(e.detail.svgHtml, 'text/html');
          const targetSvg = targetDoc.querySelector('svg');
          const targetElements = targetSvg ? targetSvg.getAttribute('data-elements') : null;

          if (targetElements && window.svgDiagrams) {
            for (const [key, val] of Object.entries(window.svgDiagrams)) {
              const valDoc = parser.parseFromString(val, 'text/html');
              const valSvg = valDoc.querySelector('svg');
              const valElements = valSvg ? valSvg.getAttribute('data-elements') : null;
              
              if (valElements === targetElements) {
                matchedShortcode = key;
                break;
              }
            }
          }

          if (!matchedShortcode && window.svgDiagrams) {
            for (const [key, val] of Object.entries(window.svgDiagrams)) {
              if (val === e.detail.svgHtml || val.trim() === e.detail.svgHtml.trim()) {
                matchedShortcode = key;
                break;
              }
            }
          }

          let startPos = -1;
          let endPos = -1;

          if (matchedShortcode) {
            const matchIndex = currentVal.indexOf(matchedShortcode);
            if (matchIndex !== -1) {
              startPos = matchIndex;
              endPos = matchIndex + matchedShortcode.length;
              setActiveShortcode(matchedShortcode);
              setInitialSvg(window.svgDiagrams[matchedShortcode]);
              setReplaceRange({ start: startPos, end: endPos });
            }
          }

          // Fallback: search raw XML SVG in text if we couldn't find/match a shortcode
          if (startPos === -1) {
            setActiveShortcode(null);
            if (targetElements) {
              const escapedMeta = targetElements.replace(/"/g, '&quot;');
              let metaIndex = currentVal.indexOf(escapedMeta);
              if (metaIndex === -1) {
                metaIndex = currentVal.indexOf(targetElements);
              }
              if (metaIndex !== -1) {
                const openIndex = currentVal.lastIndexOf('<svg', metaIndex);
                const closeIndex = currentVal.indexOf('</svg>', metaIndex);
                if (openIndex !== -1 && closeIndex !== -1) {
                  startPos = openIndex;
                  endPos = closeIndex + 6;
                }
              }
            }

            if (startPos === -1) {
              const plainSvg = e.detail.svgHtml;
              let matchIndex = currentVal.indexOf(plainSvg);
              if (matchIndex === -1) {
                matchIndex = currentVal.indexOf(plainSvg.trim());
              }
              if (matchIndex !== -1) {
                startPos = matchIndex;
                endPos = matchIndex + plainSvg.trim().length;
              } else {
                const openIndex = currentVal.indexOf('<svg');
                const closeIndex = currentVal.indexOf('</svg>');
                if (openIndex !== -1 && closeIndex !== -1) {
                  startPos = openIndex;
                  endPos = closeIndex + 6;
                }
              }
            }

            if (startPos !== -1 && endPos !== -1) {
              // Convert raw SVG to a new shortcode for premium editing!
              const nextIdx = Object.keys(window.svgDiagrams || {}).length + 1;
              const newShortcode = `[SVG-${nextIdx}]`;
              window.svgDiagrams = window.svgDiagrams || {};
              window.svgDiagrams[newShortcode] = currentVal.substring(startPos, endPos);
              setActiveShortcode(newShortcode);
              
              setReplaceRange({ start: startPos, end: endPos });
              setInitialSvg(window.svgDiagrams[newShortcode]);
            } else {
              setReplaceRange(null);
            }
          }
        }

        setShowModal(true);
        if (onOpen) onOpen();
      }
    };

    window.addEventListener('edit-svg-diagram', handleEditSvg);
    return () => window.removeEventListener('edit-svg-diagram', handleEditSvg);
  }, [targetId, onOpen]);

  return (
    <>
      <div style={{ display: 'inline-block' }}>
        <button
          type="button"
          onClick={openModal}
          title="SVG Diagram Editor"
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#8b5cf6', // Premium Indigo/Purple for Drawing
            color: 'white',
            border: 'none',
            boxShadow: 'var(--shadow-sm)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
        >
          <i className="fas fa-pen-nib" style={{ fontSize: '1rem' }}></i>
        </button>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); if (onClose) onClose(); }}
        title="SVG Editor"
        hideHeader={true}
        style={{ width: '100%', maxWidth: '100%', height: '100%', maxHeight: '100%', borderRadius: 0, display: 'flex', flexDirection: 'column' }}
      >
         <SvgEditor initialSvg={initialSvg} onSave={handleInsertToMain} onCancel={() => { setShowModal(false); if (onClose) onClose(); }} />
      </Modal>
    </>
  );
};

export default SvgToolbar;
